import {
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    Typography,
} from "@mui/material";
import { getTranslation } from "common";
import { useSnackbar, useTranslations } from "hooks";
import React, { useCallback, useEffect, useState } from "react";
import { Add, Clear } from "@mui/icons-material";
import { UsersMenu } from "./menus";
import { AssociatedUser } from "./UserItem";
import { rolesApi } from "api";
import AvatarRem from "./AvatarRem";


interface RoleSelectedPanelProps {
    roleId: number | null;
    organizationId: number;
    inMobile?: boolean;
    administrator: boolean;
}


const RoleSelectedPanel: React.FC<RoleSelectedPanelProps> = ({
    roleId,
    organizationId,
    inMobile,
    administrator,
}) => {
    const translations = useTranslations();
    const [userMenu, setUserMenu] = useState<null | HTMLElement>(null);
    const { sendSnack } = useSnackbar();
    const [associatedUsers, setAssociatedUsers] = useState<AssociatedUser[]>([]);

    const refreshUsers = useCallback(async () => {
        const { data } = await rolesApi.get(`/${roleId}/users`);
        setAssociatedUsers(data.users);
    }, [roleId]);

    useEffect(() => {
        refreshUsers();
    }, [refreshUsers]);


    const linkUser = async (id: number) => {
        try {
            const createRes = await rolesApi.post(`${roleId}/users/${id}`);
            refreshUsers();
            sendSnack({
                message: `${createRes.data.responsemessage}`,
                type: "success",
            });
            setUserMenu(null);
        } catch {
        } finally {
        }
    };
    const unlinkUser = async (id: number) => {
        try {
            const deleteApi = await rolesApi.delete(`${roleId}/users/${id}`);
            refreshUsers();
            sendSnack({
                message: `${deleteApi.data.responsemessage}`,
                type: "success",
            });
            setUserMenu(null);
        } catch {
        } finally {
        }
    };


    return (
        <React.Fragment>
            <UsersMenu
                anchor={userMenu}
                associated={associatedUsers}
                onClose={() => setUserMenu(null)}
                onLink={linkUser}
                organizationId={organizationId}
            />
            {inMobile || <Divider flexItem orientation="vertical" sx={{ m: 1 }} />}

            <Stack maxWidth={300} height="100%" spacing={1} flex={1}>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    justifyContent="space-between"
                >
                    <Typography variant="subtitle1">
                        {getTranslation(translations, "users.name")}
                    </Typography>
                    {administrator && (
                        <IconButton
                            id="add-users"
                            onClick={({ currentTarget }) => setUserMenu(currentTarget)}
                            size="medium"
                            color="info"
                        >
                            <Add fontSize="medium" />
                        </IconButton>
                    )}
                </Stack>
                <List id="associated-users" sx={{ height: "100%", overflow: "auto" }} disablePadding>
                    {associatedUsers.map(({ firstname, lastname, id, picture }) => (
                        <ListItem alignItems="center" key={`associated-users-${id}`}>
                            <ListItemAvatar sx={{ height: "40px", width: "40px" }}>
                                <AvatarRem icon={picture || ""}>
                                    {firstname[0]}
                                    {lastname[0]}
                                </AvatarRem>
                            </ListItemAvatar>
                            <ListItemText>
                                {firstname} {lastname}
                            </ListItemText>
                            <ListItemSecondaryAction>
                                {administrator && (
                                    <IconButton
                                        id={`remove-user-${id}`}
                                        size="medium"
                                        color="error"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            unlinkUser(id);
                                        }}
                                    >
                                        <Clear fontSize="medium" />
                                    </IconButton>
                                )}
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            </Stack>
        </React.Fragment>
    );
};

export default RoleSelectedPanel;
